import ActivityEntity from "../entities/activityEntity";
import {
  ActivityStatusChangeEventContext,
  EntityEvent,
  EntityEventContext,
  EntityEventType
} from "../contracts/entityEvent";
import Activity from "../entities/activity";
import ActivityWindow from "../entities/activityWindow";
import { ActivityState } from "..//contracts/entityEvent";

declare const htmlContainer: any;

/**
 * Collection of ActivityEntities that provides change notifications
 * @module entityCollection
 * @private
 */
export class EntityObservableCollection<T extends ActivityEntity> {

  private _items: { [key: string]: T } = {};
  private _listeners: ((item: T, action: EntityEventContext) => void)[] = [];
  private _processNew: (entity: T) => void;

  constructor(processNew: (entity: T) => void) {
    this._processNew = processNew;
  }

  /**
   * Adds one item to the collection
   */
  public addOne(item: T) {
    this.add([item]);
  }

  /**
   * Adds an array of items to the collection
   */
  public add(items: T[]) {
    items.forEach((element) => {
      this.process(new EntityEvent<T>(element, new EntityEventContext(EntityEventType.Added)));
    });
  }

  /**
   * Process existing EntityEvent
   */
  public process(event: EntityEvent<T>): T {

    const context = event.context;
    const type = context.type;
    const entity = event.entity;

    // here we're populating some data which particular implementations (e.g.
    // GW3) can't provide

    // TODO: extract these cases into external filters

    // activity status change without old status?
    // grab it from the existing entity
    if (type === EntityEventType.StatusChange &&
      !(context as ActivityStatusChangeEventContext).oldStatus) {
      const act = this._items[entity.id] as any as Activity;
      if (act) {
        (context as ActivityStatusChangeEventContext).oldStatus = act.status;
      }
    }

    // the GW3 bridge notifies us of possible status changes (it's mostly stateless so
    // it can't/doesn't distinguish between JOINED ACTIVITY for an activity we already
    // know about vs a brand new activity. for this reason, we just coalesce status
    // updates which indicate the same state.
    if (type === EntityEventType.StatusChange &&
      (context as ActivityStatusChangeEventContext).oldStatus &&
      (context as ActivityStatusChangeEventContext).newStatus &&
      (context as ActivityStatusChangeEventContext).oldStatus.state ===
      (context as ActivityStatusChangeEventContext).newStatus.state) {
      context.type = EntityEventType.Updated;
    }

    // GW3: coalesce multiple joined/left events

    if (typeof htmlContainer === "undefined") {
      if (type === EntityEventType.ActivityWindowJoinedActivity &&
        this._items[entity.id] &&
        (this._items[entity.id] as any as ActivityWindow).activity) {
        context.type = EntityEventType.Updated;
      }
      if (type === EntityEventType.ActivityWindowLeftActivity &&
        this._items[entity.id] &&
        !(this._items[entity.id] as any as ActivityWindow).activity) {
        context.type = EntityEventType.Updated;
      }
    }

    // update internal collections (adding the entity or updating existing entity). From that
    // point start using the internalEntity
    // NB: undefined properties are coalesced, null properties represent removals
    const internalEntity = this._updateInternalCollections(entity, type, context);
    this._notifyListeners(internalEntity, context);
    return internalEntity;
  }

  /**
   * Gets all items from the collection.
   */
  public get(): T[] {
    const result = [];
    for (const key in this._items) {
      if (this._items.hasOwnProperty(key)) {
        const element = this._items[key];
        result.push(element);
      }
    }
    return result;
  }

  /**
   * Gets item by name/id
   * @param name  Name/id of the item to get
   * @returns     Item if found; undefined if not
   */
  public getByName(name: string) {
    // this does not work for some reason
    // return this._items[name];
    for (const key in this._items) {
      if (key === name) {
        return this._items[key];
      }
    }
    return undefined;
  }

  /**
   * Gets a item if in the collection or waits for it to appear
   * @param name
   * @returns {Promise<T>}
   */
  public getOrWait(name: string): Promise<T> {
    return new Promise<T>((resolve) => {
      const entityAddedHandler = (entity: T) => {
        if (entity.id !== name) {
          return;
        }
        resolve(entity);
        this.unsubscribe(entityAddedHandler);
      };
      this.subscribe(entityAddedHandler);

      // try to get right away - if not here wait for it to appear
      const window = this.getByName(name);
      if (window) {
        this.unsubscribe(entityAddedHandler);
        resolve(window);
        return;
      }
    });
  }

  /**
   * Subscribes a listener for changes
   * @param handler Callback for changes
   */
  public subscribe(handler: (act: T, action: EntityEventContext) => void): () => void {
    this._listeners.push(handler);
    // replay existing items for new subscribers
    Object.keys(this._items).forEach((key) => {
      const element = this._items[key];
      handler(element, new EntityEventContext(EntityEventType.Added.toString()));
    });

    return () => {
      this.unsubscribe(handler);
    };
  }

  /**
   * Unsubscribes a listener
   */
  public unsubscribe(handler: (act: T, action: EntityEventContext) => void) {
    const index = this._listeners.indexOf(handler);
    if (index !== -1) {
      this._listeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners for some event
   */
  private _notifyListeners(entity: T, context: EntityEventContext) {
    this._listeners.forEach((listener) => {
      try {
        listener(entity, context);
      } catch (e) {
        return;
      }
    });
  }

  /**
   * Update internal collections (usually after some kind of modification event has arrived)
   */
  private _updateInternalCollections(entity: T, type: EntityEventType, context: any) {
    const entityAsAny = (entity as any);

    // #deleteme TODO: refactor - currently we apply some rules for each type
    const isActivityDestroy =
      (type === EntityEventType.StatusChange &&
        entityAsAny.status &&
        entityAsAny.status.state === ActivityState.Destroyed) ||
      (type === EntityEventType.StatusChange &&
        context &&
        context.newStatus &&
        context.newStatus.state === ActivityState.Destroyed);

    // #deleteme TODO: verify this stuff
    // windows have two states - removed from activity and closed (TODO: this only works in GD 2.1 and later)
    const isWindowClose = type === EntityEventType.Closed;
    const isTypeRemove = type === EntityEventType.Removed && typeof entityAsAny.isIndependent === "undefined";

    if (isTypeRemove || isWindowClose || isActivityDestroy) {

      const oldEntity = this._items[entity.id];
      delete this._items[entity.id];
      // on remove return the input entity
      this._processNew(entity);
      if (oldEntity) {
        entity._beforeDelete(oldEntity);
      }
      return entity;
    } else {
      const key = entity.id;
      // if new object add, otherwise clone object properties
      if (!this._items.hasOwnProperty(key)) {
        this._processNew(entity);
        this._items[entity.id] = entity;
      } else {
        this._items[entity.id]._update(entity);
      }
    }

    return this._items[entity.id];
  }
}
